import React, { useState, useContext, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip,
  Avatar,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Snackbar,
  LinearProgress,
  Collapse,
  CardActions,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  MusicNote as MusicNoteIcon,
  Album as AlbumIcon,
  Image as ImageIcon,
  DragHandle as DragHandleIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  LibraryMusic as LibraryMusicIcon,
  AudioFile as AudioFileIcon,
  VolumeUp as VolumeUpIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { LoginInfo } from '../../App';
import Header from '../Header';
import Footer from '../Footer';
import ApiFetch from '../ApiFetch';
import './MusicUpload.css';

const MusicUpload = () => {
  const { loginInfo } = useContext(LoginInfo);
  const [activeStep, setActiveStep] = useState(0);
  
  // トラック管理
  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState({
    file: null,
    title: '',
    description: '',
    duration: 0,
    genre: '',
    bpm: '',
    key: '',
    tags: [],
    ageLimit: 1, // 1:全年齢 2:PG-12 3:R-15 4:R-18 5:R-18G
  });
  
  // アルバム管理
  const [albumMode, setAlbumMode] = useState(false);
  const [albumInfo, setAlbumInfo] = useState({
    title: '',
    description: '',
    releaseDate: '',
    coverArt: null,
    coverArtPreview: null,
    ageLimit: 1, // 1:全年齢 2:PG-12 3:R-15 4:R-18 5:R-18G
  });
  
  // UI状態
  const [tagInput, setTagInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [playingTrack, setPlayingTrack] = useState(null);
  const [expandedTrack, setExpandedTrack] = useState(null);
  
  // オーディオプレーヤー
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  
  // アルバムダイアログ
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [editingAlbumIndex, setEditingAlbumIndex] = useState(null);

  // 年齢制限ラベル取得
  const getAgeLimitLabel = (ageLimit) => {
    const labels = {
      1: '全年齢',
      2: 'PG-12',
      3: 'R-15',
      4: 'R-18',
      5: 'R-18G'
    };
    return labels[ageLimit] || '全年齢';
  };

  // オーディオプレーヤーのクリーンアップ
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // ステップ定義
  const steps = albumMode 
    ? ['アルバム情報', 'トラック追加', '確認と投稿']
    : ['トラック情報', '確認と投稿'];

  // 音声ファイル選択ハンドラ
  const handleAudioFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // MP3形式のみ許可
    if (file.type !== 'audio/mpeg' && !file.name.toLowerCase().endsWith('.mp3')) {
      setError('MP3形式のファイルのみアップロード可能です');
      return;
    }

    // ファイルサイズチェック (100MB制限)
    if (file.size > 100 * 1024 * 1024) {
      setError('ファイルサイズは100MB以下にしてください');
      return;
    }

    // 音声の長さを取得
    const audio = new Audio();
    audio.src = URL.createObjectURL(file);
    audio.onloadedmetadata = () => {
      setCurrentTrack({
        ...currentTrack,
        file: file,
        duration: Math.floor(audio.duration),
        title: file.name.replace(/\.[^/.]+$/, ''), // 拡張子を除いたファイル名
      });
    };

    setError(null);
  };

  // カバーアート選択ハンドラ
  const handleCoverArtChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }

    // プレビュー作成
    const reader = new FileReader();
    reader.onload = (e) => {
      setAlbumInfo({
        ...albumInfo,
        coverArt: file,
        coverArtPreview: e.target.result,
      });
    };
    reader.readAsDataURL(file);
    setError(null);
  };

  // タグ追加
  const handleAddTag = () => {
    if (tagInput.trim() && !currentTrack.tags.includes(tagInput.trim())) {
      setCurrentTrack({
        ...currentTrack,
        tags: [...currentTrack.tags, tagInput.trim()],
      });
      setTagInput('');
    }
  };

  // タグ削除
  const handleDeleteTag = (tagToDelete) => {
    setCurrentTrack({
      ...currentTrack,
      tags: currentTrack.tags.filter((tag) => tag !== tagToDelete),
    });
  };

  // トラック追加
  const handleAddTrack = () => {
    if (!currentTrack.file || !currentTrack.title) {
      setError('ファイルとタイトルは必須です');
      return;
    }

    setTracks([...tracks, { ...currentTrack, id: Date.now() }]);
    setCurrentTrack({
      file: null,
      title: '',
      description: '',
      duration: 0,
      genre: '',
      bpm: '',
      key: '',
      tags: [],
      ageLimit: 1,
    });
    setError(null);

    if (albumMode) {
      // アルバムモードの場合は続けて追加
      setActiveStep(1);
    }
  };

  // トラック削除
  const handleDeleteTrack = (trackId) => {
    setTracks(tracks.filter((track) => track.id !== trackId));
  };

  // トラック並び替え
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(tracks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTracks(items);
  };

  // 時間フォーマット
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // オーディオ再生/停止
  const togglePlayPause = (track) => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current.currentTime);
      });
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setPlayingTrack(null);
      });
    }

    if (playingTrack?.id === track.id && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      if (playingTrack?.id !== track.id) {
        const url = URL.createObjectURL(track.file);
        audioRef.current.src = url;
      }
      audioRef.current.play();
      setIsPlaying(true);
      setPlayingTrack(track);
    }
  };

  // 投稿処理
  const handleSubmit = async () => {
    if (tracks.length === 0) {
      setError('少なくとも1つのトラックを追加してください');
      return;
    }

    if (albumMode && !albumInfo.title) {
      setError('アルバムタイトルは必須です');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      
      // アルバム情報
      if (albumMode) {
        formData.append('albumMode', 'true');
        formData.append('albumTitle', albumInfo.title);
        formData.append('albumDescription', albumInfo.description);
        formData.append('albumReleaseDate', albumInfo.releaseDate || new Date().toISOString().split('T')[0]);
        formData.append('albumAgeLimit', albumInfo.ageLimit || 1);
        if (albumInfo.coverArt) {
          formData.append('coverArt', albumInfo.coverArt);
        }
      } else {
        formData.append('albumMode', 'false');
      }

      // トラック情報
      tracks.forEach((track, index) => {
        formData.append(`track_${index}_file`, track.file);
        formData.append(`track_${index}_title`, track.title);
        formData.append(`track_${index}_description`, track.description || '');
        formData.append(`track_${index}_duration`, Math.floor(track.duration));
        formData.append(`track_${index}_genre`, track.genre || '');
        formData.append(`track_${index}_bpm`, track.bpm || '');
        formData.append(`track_${index}_key`, track.key || '');
        formData.append(`track_${index}_age_limit`, track.ageLimit || 1);
        if (track.tags && track.tags.length > 0) {
          formData.append(`track_${index}_tags`, JSON.stringify(track.tags));
        }
      });

      // プログレスバーを段階的に更新（fetch APIでは実際の進捗は取れないため擬似的に）
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + 10;
        });
      }, 500);

      // アップロード
      const response = await new ApiFetch().formDataUpload(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/music/upload`,
        formData
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'アップロードに失敗しました');
      }

      const result = await response.json();
      
      if (!result.result) {
        throw new Error(result.error || 'アップロードに失敗しました');
      }

      // 成功処理
      setSuccessMessage(albumMode ? 'アルバムのアップロードが完了しました！' : 'トラックのアップロードが完了しました！');
      
      // リセット
      setTracks([]);
      setCurrentTrack({
        file: null,
        title: '',
        description: '',
        duration: 0,
        genre: '',
        bpm: '',
        key: '',
        tags: [],
        ageLimit: 1,
      });
      setAlbumInfo({
        title: '',
        description: '',
        releaseDate: '',
        coverArt: null,
        coverArtPreview: null,
        ageLimit: 1,
      });
      setActiveStep(0);
      
      // 音声プレーヤーをクリーンアップ
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      setPlayingTrack(null);
      
    } catch (err) {
      console.error('Upload error:', err);
      setError('アップロードに失敗しました: ' + (err.message || '不明なエラー'));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // ステップコンテンツのレンダリング
  const renderStepContent = (step) => {
    if (albumMode) {
      switch (step) {
        case 0: // アルバム情報
          return (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      minHeight: 300,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  >
                    {albumInfo.coverArtPreview ? (
                      <>
                        <Box
                          component="img"
                          src={albumInfo.coverArtPreview}
                          sx={{
                            width: '100%',
                            maxWidth: 250,
                            height: 'auto',
                            borderRadius: 2,
                            mb: 2,
                          }}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => setAlbumInfo({
                            ...albumInfo,
                            coverArt: null,
                            coverArtPreview: null,
                          })}
                        >
                          削除
                        </Button>
                      </>
                    ) : (
                      <>
                        <ImageIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                        >
                          アルバムアートを選択
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleCoverArtChange}
                          />
                        </Button>
                        <Typography variant="caption" color="text.secondary" mt={2}>
                          推奨: 正方形、最小1000x1000px
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      required
                      label="アルバムタイトル"
                      value={albumInfo.title}
                      onChange={(e) => setAlbumInfo({
                        ...albumInfo,
                        title: e.target.value,
                      })}
                      placeholder="アルバムの名前を入力"
                    />

                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="アルバム説明"
                      value={albumInfo.description}
                      onChange={(e) => setAlbumInfo({
                        ...albumInfo,
                        description: e.target.value,
                      })}
                      placeholder="アルバムの説明を入力"
                    />

                    <TextField
                      fullWidth
                      type="date"
                      label="リリース日"
                      value={albumInfo.releaseDate}
                      onChange={(e) => setAlbumInfo({
                        ...albumInfo,
                        releaseDate: e.target.value,
                      })}
                      InputLabelProps={{ shrink: true }}
                    />

                    <FormControl fullWidth>
                      <InputLabel>年齢制限</InputLabel>
                      <Select
                        value={albumInfo.ageLimit}
                        label="年齢制限"
                        onChange={(e) => setAlbumInfo({
                          ...albumInfo,
                          ageLimit: e.target.value,
                        })}
                      >
                        <MenuItem value={1}>全年齢</MenuItem>
                        <MenuItem value={2}>PG-12（12歳以上推奨）</MenuItem>
                        <MenuItem value={3}>R-15（15歳以上）</MenuItem>
                        <MenuItem value={4}>R-18（18歳以上）</MenuItem>
                        <MenuItem value={5}>R-18G（18歳以上・グロテスク）</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                  disabled={!albumInfo.title}
                >
                  次へ: トラック追加
                </Button>
              </Box>
            </Box>
          );

        case 1: // トラック追加
          return (
            <Box>
              {renderTrackInput()}
              {tracks.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  {renderTrackList()}
                </>
              )}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => setActiveStep(0)}>
                  戻る
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(2)}
                  disabled={tracks.length === 0}
                >
                  次へ: 確認
                </Button>
              </Box>
            </Box>
          );

        case 2: // 確認
          return renderConfirmation();

        default:
          return null;
      }
    } else {
      // シングルトラックモード
      switch (step) {
        case 0: // トラック情報
          return (
            <Box>
              {renderTrackInput()}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleAddTrack();
                    setActiveStep(1);
                  }}
                  disabled={!currentTrack.file || !currentTrack.title}
                >
                  次へ: 確認
                </Button>
              </Box>
            </Box>
          );

        case 1: // 確認
          return renderConfirmation();

        default:
          return null;
      }
    }
  };

  // トラック入力フォーム
  const renderTrackInput = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        <MusicNoteIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        トラック情報
      </Typography>

      <Stack spacing={3} sx={{ mt: 2 }}>
        {/* ファイル選択 */}
        <Box>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<AudioFileIcon />}
            sx={{ py: 2 }}
          >
            {currentTrack.file ? currentTrack.file.name : 'MP3ファイルを選択'}
            <input
              type="file"
              hidden
              accept="audio/mpeg,.mp3"
              onChange={handleAudioFileChange}
            />
          </Button>
          {currentTrack.duration > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              長さ: {formatDuration(currentTrack.duration)}
            </Typography>
          )}
        </Box>

        {/* タイトル */}
        <TextField
          fullWidth
          required
          label="タイトル"
          value={currentTrack.title}
          onChange={(e) => setCurrentTrack({
            ...currentTrack,
            title: e.target.value,
          })}
          placeholder="トラック名を入力"
        />

        {/* 説明 */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="説明"
          value={currentTrack.description}
          onChange={(e) => setCurrentTrack({
            ...currentTrack,
            description: e.target.value,
          })}
          placeholder="トラックの説明を入力"
        />

        {/* メタデータ */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="ジャンル"
              value={currentTrack.genre}
              onChange={(e) => setCurrentTrack({
                ...currentTrack,
                genre: e.target.value,
              })}
              placeholder="例: Pop, Rock"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="BPM"
              type="number"
              value={currentTrack.bpm}
              onChange={(e) => setCurrentTrack({
                ...currentTrack,
                bpm: e.target.value,
              })}
              placeholder="120"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="キー"
              value={currentTrack.key}
              onChange={(e) => setCurrentTrack({
                ...currentTrack,
                key: e.target.value,
              })}
              placeholder="例: C major"
            />
          </Grid>
        </Grid>

        {/* 年齢制限 */}
        <FormControl fullWidth>
          <InputLabel>年齢制限</InputLabel>
          <Select
            value={currentTrack.ageLimit}
            label="年齢制限"
            onChange={(e) => setCurrentTrack({
              ...currentTrack,
              ageLimit: e.target.value,
            })}
          >
            <MenuItem value={1}>全年齢</MenuItem>
            <MenuItem value={2}>PG-12（12歳以上推奨）</MenuItem>
            <MenuItem value={3}>R-15（15歳以上）</MenuItem>
            <MenuItem value={4}>R-18（18歳以上）</MenuItem>
            <MenuItem value={5}>R-18G（18歳以上・グロテスク）</MenuItem>
          </Select>
        </FormControl>

        {/* タグ */}
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="タグ"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder="タグを入力してEnter"
            />
            <Button variant="outlined" onClick={handleAddTag}>
              追加
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {currentTrack.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => handleDeleteTag(tag)}
                size="small"
              />
            ))}
          </Box>
        </Box>

        {/* アルバムモードの場合は追加ボタン */}
        {albumMode && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddTrack}
            disabled={!currentTrack.file || !currentTrack.title}
          >
            トラックを追加
          </Button>
        )}
      </Stack>
    </Paper>
  );

  // トラックリスト表示
  const renderTrackList = () => (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          追加されたトラック ({tracks.length})
        </Typography>
        <Tooltip title="ドラッグして並び替え">
          <InfoIcon color="action" fontSize="small" />
        </Tooltip>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tracks">
          {(provided) => (
            <List {...provided.droppableProps} ref={provided.innerRef}>
              {tracks.map((track, index) => (
                <Draggable key={track.id} draggableId={String(track.id)} index={index}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        mb: 2,
                        border: 1,
                        borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                        boxShadow: snapshot.isDragging ? 4 : 1,
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box
                            {...provided.dragHandleProps}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              pt: 1,
                            }}
                          >
                            <DragHandleIcon color="action" />
                            <Typography variant="caption" color="text.secondary">
                              {index + 1}
                            </Typography>
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6" component="div">
                                {track.title}
                              </Typography>
                              <Chip
                                label={formatDuration(track.duration)}
                                size="small"
                                icon={<MusicNoteIcon />}
                              />
                            </Box>

                            {track.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {track.description}
                              </Typography>
                            )}

                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                              {track.genre && (
                                <Chip label={track.genre} size="small" variant="outlined" />
                              )}
                              {track.bpm && (
                                <Chip label={`${track.bpm} BPM`} size="small" variant="outlined" />
                              )}
                              {track.key && (
                                <Chip label={track.key} size="small" variant="outlined" />
                              )}
                              {track.ageLimit && (
                                <Chip 
                                  label={getAgeLimitLabel(track.ageLimit)} 
                                  size="small" 
                                  variant="outlined"
                                  color={track.ageLimit >= 4 ? 'error' : track.ageLimit >= 3 ? 'warning' : 'default'}
                                />
                              )}
                            </Stack>

                            {track.tags.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {track.tags.map((tag, idx) => (
                                  <Chip key={idx} label={tag} size="small" color="primary" />
                                ))}
                              </Box>
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title={playingTrack?.id === track.id && isPlaying ? '一時停止' : '再生'}>
                              <IconButton
                                color="primary"
                                onClick={() => togglePlayPause(track)}
                              >
                                {playingTrack?.id === track.id && isPlaying ? (
                                  <PauseIcon />
                                ) : (
                                  <PlayArrowIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="削除">
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteTrack(track.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* 再生中のプログレスバー */}
                        {playingTrack?.id === track.id && isPlaying && (
                          <Box sx={{ mt: 2 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(currentTime / track.duration) * 100}
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDuration(Math.floor(currentTime))}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDuration(track.duration)}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </Paper>
  );

  // 確認画面
  const renderConfirmation = () => (
    <Box>
      {albumMode && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <AlbumIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            アルバム情報
          </Typography>
          <Grid container spacing={2}>
            {albumInfo.coverArtPreview && (
              <Grid item xs={12} sm={4}>
                <Box
                  component="img"
                  src={albumInfo.coverArtPreview}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={albumInfo.coverArtPreview ? 8 : 12}>
              <Typography variant="h5" gutterBottom>
                {albumInfo.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {albumInfo.description}
              </Typography>
              {albumInfo.releaseDate && (
                <Typography variant="body2">
                  リリース日: {albumInfo.releaseDate}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>
                年齢制限: {getAgeLimitLabel(albumInfo.ageLimit)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                トラック数: {tracks.length}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {renderTrackList()}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={() => setActiveStep(activeStep - 1)}>
          戻る
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={isUploading || tracks.length === 0}
          startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
        >
          {isUploading ? `アップロード中 ${uploadProgress}%` : '投稿する'}
        </Button>
      </Box>
    </Box>
  );

  // ログインチェック
  useEffect(() => {
    if (loginInfo.result === false) {
      window.location.href = '/';
    }
  }, [loginInfo]);

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }} className="music-upload-container">
        <Paper sx={{ p: 4 }}>
          {/* ヘッダー */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              <LibraryMusicIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 40 }} />
              音楽投稿
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI生成の音楽をアップロードして共有しましょう
            </Typography>
          </Box>

          {/* アルバムモード切り替え */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={albumMode}
                  onChange={(e) => {
                    setAlbumMode(e.target.checked);
                    setActiveStep(0);
                    setTracks([]);
                  }}
                  disabled={isUploading}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    アルバムモード
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    複数トラックをまとめてアルバムとして投稿
                  </Typography>
                </Box>
              }
            />
          </Paper>

          {/* エラー表示 */}
          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError(null)} 
              sx={{ mb: 3 }}
              className="error-message"
            >
              {error}
            </Alert>
          )}

          {/* アップロード中のプログレス */}
          {isUploading && (
            <Paper sx={{ p: 3, mb: 3 }} className="uploading-indicator">
              <Typography variant="h6" gutterBottom>
                アップロード中...
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ mb: 1, height: 8, borderRadius: 4 }}
                className="upload-progress-bar"
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {uploadProgress}%
              </Typography>
            </Paper>
          )}

          {/* ステッパー */}
          <Stepper activeStep={activeStep} orientation="vertical" className="custom-stepper">
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  optional={
                    index === steps.length - 1 && (
                      <Typography variant="caption">最終ステップ</Typography>
                    )
                  }
                >
                  {label}
                </StepLabel>
                <StepContent>
                  {renderStepContent(index)}
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {/* 完了時のメッセージ */}
          {activeStep === steps.length && (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'success.light' }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                投稿完了！
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {albumMode ? 'アルバム' : 'トラック'}が正常にアップロードされました
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setActiveStep(0);
                  window.scrollTo(0, 0);
                }}
              >
                新しい投稿を作成
              </Button>
            </Paper>
          )}
        </Paper>

        {/* 使い方のヒント */}
        <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            <InfoIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            使い方
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.primary">
              • 対応フォーマット: MP3のみ
            </Typography>
            <Typography variant="body2" color="text.primary">
              • 最大ファイルサイズ: 100MB
            </Typography>
            <Typography variant="body2" color="text.primary">
              • アルバムモード: 複数のトラックをまとめて1つのアルバムとして投稿できます
            </Typography>
            <Typography variant="body2" color="text.primary">
              • トラックの順序: ドラッグ&ドロップで自由に並び替えが可能です
            </Typography>
            <Typography variant="body2" color="text.primary">
              • プレビュー: 各トラックの再生ボタンで投稿前に確認できます
            </Typography>
          </Stack>
        </Paper>
      </Container>

      {/* 成功メッセージのスナックバー */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage('')} 
          severity="success" 
          sx={{ width: '100%' }}
          variant="filled"
          className="success-message"
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* エラーメッセージのスナックバー */}
      <Snackbar
        open={!!error}
        autoHideDuration={8000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error" 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {error}
        </Alert>
      </Snackbar>

      <Footer />
    </>
  );
};

export default MusicUpload;
